import React, { useState, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { IconDownload, IconArrowRight, IconCheck, IconRocket, IconAlertCircle } from '@tabler/icons-react';
import CreationProgress from '../components/CreationProgress';
import AgentsFlowService from '../../../services/AgentsFlowService';

const CAPABILITY_LABELS = {
  scheduling: 'Agendamento',
  faq: 'Perguntas Frequentes',
  data_collection: 'Captura de Dados',
  media_library: 'Galeria de Mídias',
  media_analysis: 'Análise de Mídias',
};

const TONE_LABELS = {
  professional: 'Profissional',
  friendly: 'Amigável',
  casual: 'Casual',
  formal: 'Formal',
};

const CreationStep = ({ wizardState, setWizardState, onComplete }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const { idealizationSummary, technicalPlan, knowledgeFileKey, knowledgeOption, createdAgent } = wizardState;
  const hasKnowledge = knowledgeOption === 'upload' && !!knowledgeFileKey;

  const handleCreate = useCallback(async () => {
    setIsCreating(true);
    setError(null);
    setProgressStep(0);

    try {
      // Simulate progress steps visually
      const totalSteps = hasKnowledge ? 5 : 4;
      const stepDelay = 600;

      const advanceProgress = (step) => {
        return new Promise((resolve) => {
          setTimeout(() => {
            setProgressStep(step);
            resolve();
          }, stepDelay);
        });
      };

      await advanceProgress(1);

      const response = await AgentsFlowService.wizardCreate({
        idealization_summary: idealizationSummary,
        technical_plan: technicalPlan,
        knowledge_file_key: hasKnowledge ? knowledgeFileKey : null,
        knowledge_option: knowledgeOption,
      });

      // Advance remaining steps visually
      for (let i = 2; i <= totalSteps; i++) {
        await advanceProgress(i);
      }

      setWizardState((prev) => ({
        ...prev,
        createdAgent: response.data,
        pdfGuideUrl: response.data.pdf_generated ? response.data.id : null,
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar agente. Tente novamente.');
    } finally {
      setIsCreating(false);
    }
  }, [idealizationSummary, technicalPlan, knowledgeFileKey, knowledgeOption, hasKnowledge, setWizardState]);

  const handleDownloadPdf = async () => {
    if (!createdAgent?.id) return;

    setIsDownloading(true);
    setDownloadError(null);

    try {
      const response = await AgentsFlowService.getWizardGuide(createdAgent.id);
      const contentType = response.headers?.['content-type'] || 'application/pdf';
      const isHtml = contentType.includes('text/html');

      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `guia-${createdAgent.name || 'agente'}.${isHtml ? 'html' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 404) {
        setDownloadError('O guia ainda está sendo gerado. Tente novamente em alguns segundos.');
      } else {
        setDownloadError('Erro ao baixar o guia. Tente novamente.');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Success state
  if (createdAgent) {
    return (
      <div className="awc-success">
        <div className="awc-success__icon">
          <IconCheck size={40} />
        </div>
        <h2>Agente criado com sucesso!</h2>
        <p>
          <strong>{createdAgent.name}</strong> está pronto para ser configurado.
          <br />
          Baixe o guia para saber os próximos passos.
        </p>
        <div className="awc-success__actions">
          {createdAgent.pdf_generated ? (
            <button
              className="awc-btn awc-btn--outline"
              onClick={handleDownloadPdf}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Baixando...
                </>
              ) : (
                <>
                  <IconDownload size={16} />
                  Baixar Guia
                </>
              )}
            </button>
          ) : (
            <span className="awc-success__note">
              <IconAlertCircle size={14} />
              Guia será disponibilizado em breve
            </span>
          )}
          <button
            className="awc-btn awc-btn--primary"
            onClick={() => onComplete(createdAgent)}
          >
            Abrir no Canvas
            <IconArrowRight size={16} />
          </button>
        </div>
        {downloadError && (
          <p className="awc-success__error">{downloadError}</p>
        )}
      </div>
    );
  }

  // Creating state
  if (isCreating) {
    return (
      <div aria-live="polite">
        <h3 className="awc-body__title text-center">Criando seu agente...</h3>
        <p className="awc-body__subtitle text-center">
          Aguarde enquanto configuramos tudo automaticamente
        </p>
        <CreationProgress currentStepIndex={progressStep} hasKnowledge={hasKnowledge} />
      </div>
    );
  }

  // Ready to create state
  return (
    <div>
      <h3 className="awc-body__title">Tudo pronto!</h3>
      <p className="awc-body__subtitle">
        Revise o resumo e clique em "Criar Agente" para gerar tudo automaticamente.
      </p>

      <div className="awc-summary mb-3">
        <h4 className="awc-summary__title">Resumo da criação</h4>
        <div className="row g-2 mt-1">
          <div className="col-md-6">
            <p className="mb-1"><strong>Agente:</strong> {technicalPlan?.identity_config?.display_name}</p>
            <p className="mb-1"><strong>Função:</strong> {technicalPlan?.identity_config?.role}</p>
            <p className="mb-1"><strong>Tom:</strong> {TONE_LABELS[technicalPlan?.identity_config?.tone] || technicalPlan?.identity_config?.tone}</p>
          </div>
          <div className="col-md-6">
            <p className="mb-1"><strong>Capacidades:</strong></p>
            <ul className="awc-summary__list">
              {(technicalPlan?.capabilities || []).map((cap, i) => (
                <li key={i}>{CAPABILITY_LABELS[cap] || cap.replace('_', ' ')}</li>
              ))}
            </ul>
          </div>
        </div>
        {hasKnowledge && (
          <p className="mt-2 mb-0 awc-text--success">
            Base de conhecimento será incluída
          </p>
        )}
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      <div className="text-center mt-4">
        <button
          className="awc-btn awc-btn--primary awc-btn--lg"
          onClick={handleCreate}
        >
          <IconRocket size={20} />
          Criar Agente
        </button>
      </div>
    </div>
  );
};

export default CreationStep;
